// Custom DS-Table column for the Primary Contact: a design-system Avatar (profile image or initials) next
// to the contact name, with the clickable mailto email underneath (preserving the legacy affordance). The
// public column API cannot express this composite cell, so we author a column (dist-internal imports are
// the accepted web-front convention for custom DS columns — see StatusPillColumn / ColoredIdIconColumn).
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import {Avatar, Link} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import Styled from 'styled-components';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';

export interface ContactColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	width?: string;
}

const ContactLayout = Styled.div`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	max-width: 100%;
`;
const ContactText = Styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;
const ContactName = Styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ContactColumn = (_: ContactColumnType): null => null;

ContactColumn.toColumnDef = (child: React.ReactElement<ContactColumnType>): ColumnDef<OpportunityTableRow> => ({
	...defaultColumnDef(child.props),
	accessorKey: child.props.accessorKey,
	sortingFn: child.props.sortingFn,
	cell: ({row}: {row: {original: OpportunityTableRow}}) => {
		const {contactName, contactEmail, contactImage} = row.original;
		if (!contactName && !contactEmail) {
			return <StyledCell align={child.props.align} data-cy="opportunity-contact-cell" />;
		}
		return (
			<StyledCell align={child.props.align} data-cy="opportunity-contact-cell">
				<ContactLayout>
					{/* data-private: profile-picture endpoints are authenticated, so LogRocket must strip the src. */}
					<Avatar size="s" name={contactName} src={contactImage} data-private />
					<ContactText>
						<ContactName>{contactName}</ContactName>
						{contactEmail ? (
							<Link href={`mailto:${contactEmail}`} data-cy="contact-email" data-private>
								{contactEmail}
							</Link>
						) : null}
					</ContactText>
				</ContactLayout>
			</StyledCell>
		);
	},
});

export default ContactColumn;
